import { useState } from "react";
import { Users, Sofa, PackageOpen, HardHat, CheckCircle2, Loader2, FileUp, X, Handshake } from "lucide-react";
import { toast } from "sonner";
import { usePageMeta } from "../hooks/usePageMeta";
import { api, formatApiErrorDetail } from "../lib/api";
import {
  WORK_INTERESTS,
  EXPERIENCE_OPTIONS,
  AVAILABILITY_OPTIONS,
  TRAVEL_OPTIONS,
  TRANSPORT_OPTIONS,
  PHYSICAL_OPTIONS,
  SUCCESS_MESSAGE,
  BACKGROUND_ACK_TEXT,
  CERTIFICATION_TEXT,
} from "../data/employment";

const WORK_TYPES = [
  {
    icon: Users,
    title: "Estate Sale Event Staff",
    text: "Customer assistance, greeting shoppers, merchandise monitoring, crowd direction, setup and breakdown.",
  },
  {
    icon: Sofa,
    title: "Furniture Moving & Loading",
    text: "Moving furniture, loading and unloading, carrying items, and assisting with safe transport.",
  },
  {
    icon: PackageOpen,
    title: "Estate Cleanout Crew",
    text: "Sorting, carrying, loading, donation preparation, removal work, and post-sale clearing.",
  },
  {
    icon: HardHat,
    title: "General On-Call Labor",
    text: "Setup, breakdown, organization, loading, unloading, and other short-term physical support.",
  },
];

const ChipGroup = ({ options, selected, onToggle, name }) => (
  <div className="flex flex-wrap gap-2.5" role="group" aria-label={name}>
    {options.map((opt) => {
      const on = selected.includes(opt);
      return (
        <button
          key={opt}
          type="button"
          onClick={() => onToggle(opt)}
          aria-pressed={on}
          className={`px-4 py-2.5 text-sm font-semibold border transition-colors ${
            on
              ? "bg-forest text-ivory border-forest"
              : "bg-white text-ink/70 border-linen hover:border-forest/50"
          }`}
          data-testid={`chip-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        >
          {opt}
        </button>
      );
    })}
  </div>
);

const RadioRow = ({ options, value, onChange, name }) => (
  <div className="flex flex-wrap gap-2.5" role="radiogroup" aria-label={name}>
    {options.map((opt) => (
      <button
        key={opt}
        type="button"
        role="radio"
        aria-checked={value === opt}
        onClick={() => onChange(opt)}
        className={`px-4 py-2.5 text-sm font-semibold border transition-colors ${
          value === opt
            ? "bg-forest text-ivory border-forest"
            : "bg-white text-ink/70 border-linen hover:border-forest/50"
        }`}
        data-testid={`radio-${name}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      >
        {opt}
      </button>
    ))}
  </div>
);

const initialForm = {
  first_name: "",
  last_name: "",
  phone: "",
  email: "",
  city: "",
  state: "",
  age_18: "",
  work_interests: [],
  experience: [],
  experience_notes: "",
  availability: [],
  availability_notes: "",
  transportation: "",
  travel_range: "",
  physical_work: "",
  background_ack: false,
  certification: false,
};

const SectionTitle = ({ n, children }) => (
  <h3 className="font-serif text-2xl font-medium text-ink flex items-baseline gap-3">
    <span className="text-brass font-semibold">{n}.</span> {children}
  </h3>
);

export default function EmploymentPage() {
  usePageMeta(
    "Employment Opportunities | MacWay Estate Services — Flexible On-Call Work in MD & Northern VA",
    "Join the MacWay team. Flexible, on-call work for estate sales, cleanouts, furniture moving, and event support across Maryland and Northern Virginia. Accept assignments that fit your schedule."
  );
  const [form, setForm] = useState(initialForm);
  const [resume, setResume] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const setVal = (key) => (val) => setForm((f) => ({ ...f, [key]: val }));
  const toggleList = (key) => (val) =>
    setForm((f) => ({
      ...f,
      [key]: f[key].includes(val) ? f[key].filter((v) => v !== val) : [...f[key], val],
    }));

  const handleResume = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const data = new FormData();
    data.append("file", file);
    try {
      const res = await api.post("/applications/resume", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResume(res.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post("/applications", {
        ...form,
        resume: resume ? { path: resume.path, original_filename: resume.original_filename, content_type: resume.content_type } : null,
        website: honeypot,
      });
      setDone(true);
      if (typeof window.gtag === "function") {
        // GA4: count the submission only — never send names, emails, or phone numbers
        window.gtag("event", "employment_application_submit", { method: "employment_form" });
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="employment-page">
      {/* Hero */}
      <section className="bg-forest text-ivory py-20 md:py-28" data-testid="employment-hero">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="eyebrow-label">Employment opportunities</p>
          <h1 className="mt-4 text-5xl sm:text-6xl font-semibold tracking-tight" data-testid="employment-headline">
            Join the MacWay Team
          </h1>
          <p className="mt-4 text-xl sm:text-2xl font-serif italic text-brass">
            Flexible, On-Call Work. Accept Assignments That Fit Your Schedule.
          </p>
          <p className="mt-6 text-base sm:text-lg text-ivory/85 leading-relaxed max-w-2xl">
            MacWay Estate Services maintains a pool of dependable people available for estate sales, home
            cleanouts, furniture moving, setup and breakdown, and other short-term assignments throughout
            Maryland and Northern Virginia.
          </p>
          <p className="mt-4 text-sm text-ivory/65 leading-relaxed max-w-2xl">
            Assignments are offered as work becomes available. Workers may accept or decline individual
            opportunities based on their availability. Placement in the applicant pool does not guarantee a
            minimum number of assignments or hours.
          </p>
          <a href="#apply" className="btn-brass mt-9 inline-flex" data-testid="hero-apply-cta">
            Apply for On-Call Work
          </a>
        </div>
      </section>

      {/* Types of work */}
      <section className="py-16 md:py-24 bg-ivory" data-testid="employment-work-types">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="eyebrow-label">Types of work</p>
            <h2 className="mt-4 text-4xl sm:text-5xl font-medium tracking-tight text-ink">
              Short-term assignments, real variety
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {WORK_TYPES.map((w) => (
              <div key={w.title} className="bg-white border border-linen p-7" data-testid={`work-type-${w.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <w.icon className="w-8 h-8 text-brass" />
                <h3 className="mt-4 text-xl font-medium text-ink">{w.title}</h3>
                <p className="mt-2.5 text-sm text-ink/65 leading-relaxed">{w.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pay */}
      <section className="py-14 md:py-16 bg-sand" data-testid="employment-pay">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white border border-linen p-8 md:p-10 flex items-start gap-5">
            <Handshake className="w-9 h-9 text-brass shrink-0 mt-1" />
            <div>
              <h2 className="text-3xl sm:text-4xl font-medium tracking-tight text-ink">About pay</h2>
              <p className="mt-4 text-base text-ink/70 leading-relaxed">
                Pay varies by assignment, location, responsibilities, and expected hours. Full pay details will
                always be provided before you accept an assignment.
              </p>
              <p className="mt-3 text-base text-ink/70 leading-relaxed">
                Some assignments may offer payment immediately after completed work. Payment method and timing
                will be explained before each assignment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Application */}
      <section className="py-16 md:py-24 bg-ivory" id="apply" data-testid="employment-apply">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {done ? (
            <div className="bg-white border border-linen p-10 md:p-14 text-center" data-testid="application-success">
              <CheckCircle2 className="w-14 h-14 text-forest mx-auto" />
              <h2 className="text-3xl sm:text-4xl font-medium text-ink mt-6">Application received</h2>
              <p className="mt-4 text-ink/70 text-base sm:text-lg leading-relaxed max-w-xl mx-auto">
                {SUCCESS_MESSAGE}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-white border border-linen p-7 md:p-10 space-y-10" data-testid="application-form">
              <div>
                <h2 className="text-3xl sm:text-4xl font-medium tracking-tight text-ink">Quick application</h2>
                <p className="mt-3 text-sm text-ink/60 leading-relaxed">
                  No resume required, no long essays — most people finish in a few minutes from their phone.
                </p>
              </div>

              {/* Honeypot */}
              <div style={{ position: "absolute", left: "-9999px", height: 0, overflow: "hidden" }} aria-hidden="true">
                <label htmlFor="app-website">Website</label>
                <input id="app-website" type="text" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
              </div>

              {/* Basic information */}
              <fieldset>
                <SectionTitle n={1}>Basic information</SectionTitle>
                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="app-first" className="form-label">First name <span className="text-brass">*</span></label>
                    <input id="app-first" className="form-input" required autoComplete="given-name" value={form.first_name} onChange={set("first_name")} data-testid="app-first-name" />
                  </div>
                  <div>
                    <label htmlFor="app-last" className="form-label">Last name <span className="text-brass">*</span></label>
                    <input id="app-last" className="form-input" required autoComplete="family-name" value={form.last_name} onChange={set("last_name")} data-testid="app-last-name" />
                  </div>
                  <div>
                    <label htmlFor="app-phone" className="form-label">Phone number <span className="text-brass">*</span></label>
                    <input id="app-phone" type="tel" className="form-input" required autoComplete="tel" value={form.phone} onChange={set("phone")} data-testid="app-phone" />
                  </div>
                  <div>
                    <label htmlFor="app-email" className="form-label">Email address <span className="text-brass">*</span></label>
                    <input id="app-email" type="email" className="form-input" required autoComplete="email" value={form.email} onChange={set("email")} data-testid="app-email" />
                  </div>
                  <div>
                    <label htmlFor="app-city" className="form-label">City <span className="text-brass">*</span></label>
                    <input id="app-city" className="form-input" required autoComplete="address-level2" value={form.city} onChange={set("city")} data-testid="app-city" />
                  </div>
                  <div>
                    <label htmlFor="app-state" className="form-label">State <span className="text-brass">*</span></label>
                    <input id="app-state" className="form-input" required autoComplete="address-level1" placeholder="MD, VA, or DC" value={form.state} onChange={set("state")} data-testid="app-state" />
                  </div>
                </div>
              </fieldset>

              {/* Age eligibility */}
              <fieldset>
                <SectionTitle n={2}>Age eligibility</SectionTitle>
                <p className="mt-4 text-sm font-semibold text-ink">Are you at least 18 years old? <span className="text-brass">*</span></p>
                <div className="mt-3">
                  <RadioRow options={["Yes", "No"]} value={form.age_18} onChange={setVal("age_18")} name="age" />
                </div>
              </fieldset>

              {/* Work interest */}
              <fieldset>
                <SectionTitle n={3}>What type of work are you interested in?</SectionTitle>
                <p className="mt-2 text-sm text-ink/60">Check all that apply. <span className="text-brass">*</span></p>
                <div className="mt-4">
                  <ChipGroup options={WORK_INTERESTS} selected={form.work_interests} onToggle={toggleList("work_interests")} name="work interests" />
                </div>
              </fieldset>

              {/* Experience */}
              <fieldset>
                <SectionTitle n={4}>What experience do you have?</SectionTitle>
                <p className="mt-2 text-sm text-ink/60">Check all that apply. <span className="text-brass">*</span></p>
                <div className="mt-4">
                  <ChipGroup options={EXPERIENCE_OPTIONS} selected={form.experience} onToggle={toggleList("experience")} name="experience" />
                </div>
                <div className="mt-5">
                  <label htmlFor="app-exp-notes" className="form-label">Anything else you want us to know about your experience? <span className="font-normal text-ink/50">(optional)</span></label>
                  <textarea id="app-exp-notes" rows={2} className="form-input resize-y" value={form.experience_notes} onChange={set("experience_notes")} data-testid="app-experience-notes" />
                </div>
              </fieldset>

              {/* Availability */}
              <fieldset>
                <SectionTitle n={5}>When are you usually available?</SectionTitle>
                <p className="mt-2 text-sm text-ink/60">Check all that apply. <span className="text-brass">*</span></p>
                <div className="mt-4">
                  <ChipGroup options={AVAILABILITY_OPTIONS} selected={form.availability} onToggle={toggleList("availability")} name="availability" />
                </div>
                <div className="mt-5">
                  <label htmlFor="app-avail-notes" className="form-label">Anything we should know about your availability? <span className="font-normal text-ink/50">(optional)</span></label>
                  <textarea id="app-avail-notes" rows={2} className="form-input resize-y" value={form.availability_notes} onChange={set("availability_notes")} data-testid="app-availability-notes" />
                </div>
              </fieldset>

              {/* Transportation */}
              <fieldset>
                <SectionTitle n={6}>Transportation</SectionTitle>
                <p className="mt-4 text-sm font-semibold text-ink">Do you have reliable transportation to work assignments? <span className="text-brass">*</span></p>
                <div className="mt-3">
                  <RadioRow options={TRANSPORT_OPTIONS} value={form.transportation} onChange={setVal("transportation")} name="transportation" />
                </div>
                <p className="mt-6 text-sm font-semibold text-ink">How far are you generally willing to travel? <span className="text-brass">*</span></p>
                <div className="mt-3">
                  <RadioRow options={TRAVEL_OPTIONS} value={form.travel_range} onChange={setVal("travel_range")} name="travel" />
                </div>
              </fieldset>

              {/* Physical work */}
              <fieldset>
                <SectionTitle n={7}>Physical work</SectionTitle>
                <p className="mt-4 text-sm font-semibold text-ink leading-relaxed">
                  Are you comfortable with assignments that may involve standing, walking, lifting, carrying,
                  loading, or moving items? <span className="text-brass">*</span>
                </p>
                <div className="mt-3">
                  <RadioRow options={PHYSICAL_OPTIONS} value={form.physical_work} onChange={setVal("physical_work")} name="physical" />
                </div>
              </fieldset>

              {/* Optional resume */}
              <fieldset>
                <SectionTitle n={8}>Resume <span className="text-base text-ink/50 font-sans font-normal">(optional)</span></SectionTitle>
                <p className="mt-2 text-sm text-ink/60">Helpful but not required — you can submit without one.</p>
                <div className="mt-4 flex items-center gap-4">
                  {resume ? (
                    <span className="inline-flex items-center gap-2 bg-sand border border-linen px-4 py-2.5 text-sm text-ink/80" data-testid="resume-chip">
                      {resume.original_filename}
                      <button type="button" onClick={() => setResume(null)} aria-label="Remove resume" className="text-ink/40 hover:text-red-700" data-testid="resume-remove">
                        <X className="w-4 h-4" />
                      </button>
                    </span>
                  ) : (
                    <label className="inline-flex items-center gap-2 cursor-pointer border-2 border-dashed border-linen hover:border-forest px-5 py-3 text-sm font-semibold text-ink/60 hover:text-forest transition-colors" data-testid="resume-upload-label">
                      {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileUp className="w-4 h-4" />}
                      {uploading ? "Uploading…" : "Upload resume (PDF, Word, or image)"}
                      <input type="file" className="hidden" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={handleResume} data-testid="resume-upload-input" />
                    </label>
                  )}
                </div>
              </fieldset>

              {/* Background screening acknowledgment */}
              <fieldset className="border-t border-linen pt-8">
                <div className="flex items-start gap-3">
                  <input
                    id="app-background"
                    type="checkbox"
                    className="mt-1 w-4 h-4 accent-[#1A3626]"
                    checked={form.background_ack}
                    onChange={(e) => setForm((f) => ({ ...f, background_ack: e.target.checked }))}
                    required
                    data-testid="app-background-ack"
                  />
                  <label htmlFor="app-background" className="text-sm text-ink/70 leading-relaxed">
                    {BACKGROUND_ACK_TEXT}
                  </label>
                </div>
              </fieldset>

              {/* Certification */}
              <fieldset>
                <div className="flex items-start gap-3">
                  <input
                    id="app-certification"
                    type="checkbox"
                    className="mt-1 w-4 h-4 accent-[#1A3626]"
                    checked={form.certification}
                    onChange={(e) => setForm((f) => ({ ...f, certification: e.target.checked }))}
                    required
                    data-testid="app-certification"
                  />
                  <label htmlFor="app-certification" className="text-sm text-ink/70 leading-relaxed">
                    {CERTIFICATION_TEXT}
                  </label>
                </div>
              </fieldset>

              {error && (
                <p className="text-sm font-semibold text-red-700 bg-red-50 border border-red-200 px-4 py-3" role="alert" data-testid="application-error">
                  {error}
                </p>
              )}

              <button type="submit" disabled={submitting || uploading} className="btn-brass w-full sm:w-auto disabled:opacity-60" data-testid="application-submit">
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Submitting…
                  </>
                ) : (
                  "Join the MacWay Crew"
                )}
              </button>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}
